import {
  ActionBarPrimitive,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessage,
  useThread,
  useThreadListItem,
  useThreadRuntime,
} from "@assistant-ui/react";
import { FC, forwardRef, useEffect, useRef, useState } from "react";
import {
  ArrowDownIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  SendHorizontalIcon,
  Star,
} from "lucide-react";
import { cn, formatCreatedAt } from "@/lib/utils";
import api from "api/axios";
import { useLocation, useNavigate } from "react-router-dom";
import { validate as isValidUUID } from "uuid";

import { Button } from "@/components/ui/button";
import { MarkdownText } from "@/components/markdown-text";
import { SourceMessagePart } from "./source-message-part";
import { useAgentContext } from "../context/AgentContext";
import { useAuthContext } from "auth/AuthProvider";
import { TooltipIconButton } from "./tooltip-icon-button";
// import { PreviewDialog } from "./preview-dialog";
import { ParentIdGroupedMessage } from "./parent-id-group";
import { ReasoningMessagePart } from "./reasoning-message-part";
import { Agent } from "types";
import { Mention, MentionsInput } from "react-mentions";
import { MessageWithMentions } from "./message-with-mentions";
import { favoriteMessage, getSuggestionQuestion } from "../../api/axios";
import { ThreadFollowupSuggestions } from "./follow-up-suggestions";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { AGENT_TYPES, AgentStatus } from "../../constants/agents";


// Wrapper to ensure the parent primitive receives a ref to the actual textarea element
// that react-mentions manages internally. The parent relies on DOM APIs like .closest().
const ComposerMentionsInput = forwardRef<HTMLTextAreaElement, any>(
  ({ children, ...props }, forwardedRef) => {
    return (
      <MentionsInput
        {...props}
        inputRef={forwardedRef as unknown as React.RefObject<HTMLTextAreaElement>}
      >
        {children}
      </MentionsInput>
    );
  }
);
ComposerMentionsInput.displayName = "ComposerMentionsInput";



// Custom copy function
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    return true;
  }
};

export const Thread: FC<{ agentInfo?: Agent }> = ({ agentInfo }) => {
  const { t } = useTranslation();
  const { selectedAgent, availableAgents } = useAgentContext();
  const { hasFullControl } = useAuthContext();
  const navigate = useNavigate();
  const threadListItem = useThreadListItem();
  const threadRuntime = useThreadRuntime();

  const [openArtifact, setOpenArtifact] = useState<{
    open: boolean;
    parentId: string;
  }>({ open: false, parentId: "" });
  const prevUserMessageCountInThread = useRef({ count: 0, threadId: "" });

  const agentForDisplay =
    agentInfo ??
    (threadListItem?.externalId
      ? availableAgents.find((a) => a.id === threadListItem.externalId) ?? null
      : selectedAgent);

  useEffect(() => {
    const unsubscribe = threadRuntime.subscribe(() => {
      const state = threadRuntime.getState();
      const messages = state.messages;

      const userMessageCount = messages.filter(
        (msg) => msg.role === "user"
      ).length;

      if (
        userMessageCount > prevUserMessageCountInThread.current.count ||
        state?.threadId !== prevUserMessageCountInThread?.current?.threadId ||
        messages.length === 0
      ) {
        setOpenArtifact({ open: false, parentId: "" });
      }

      prevUserMessageCountInThread.current = {
        count: userMessageCount,
        threadId: state?.threadId,
      };
    });

    return () => unsubscribe();
  }, [threadRuntime]);

  return (
    <>
      <ThreadPrimitive.Root
        className={cn(
          "flex h-full flex-col overflow-hidden bg-background transition-all duration-300",
          {
            ["mr-[calc(100vw/3)]"]: openArtifact?.open,
          }
        )}
        style={{
          ["--thread-max-width" as string]: "100%",
        }}
      >
        <ThreadPrimitive.Viewport className="chat-scrollbar flex h-full flex-col overflow-y-auto">
          <ThreadWelcome />

          <ThreadPrimitive.Messages
            components={{
              UserMessage: UserMessage,
              EditComposer: EditComposer,
              AssistantMessage: () => {
                return (
                  <ParentIdGroupedMessage
                    threadId={threadListItem.remoteId}
                    setOpenArtifact={setOpenArtifact}
                    openArtifact={openArtifact}
                  />
                );
              },
            }}
          />

          <ThreadFollowupSuggestions />

          <ThreadPrimitive.If empty={false}>
            <div className="min-h-8 flex-grow" />
          </ThreadPrimitive.If>

          <div className="sticky bottom-0 flex w-full flex-col items-center justify-end bg-background pb-4 pt-2">
            <ThreadScrollToBottom />
            <div className={"w-full max-w-4xl px-4"}>
              <div className="mb-1 ml-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t("chat.agent")}:{" "}
                    {agentForDisplay ? (
                      hasFullControl() ? (
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/agents/info/${agentForDisplay.id}`
                            )
                          }
                          className="text-primary hover:underline"
                        >
                          {agentForDisplay.name}
                        </button>
                      ) : (
                        <span className="text-muted-foreground">
                          {agentForDisplay.name}
                        </span>
                      )
                    ) : (
                      <span className="text-muted-foreground">
                        {t("chat.noAgentSelected")}
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <Composer agent={agentForDisplay} />
            </div>
          </div>
        </ThreadPrimitive.Viewport>
      </ThreadPrimitive.Root>

      {/* <PreviewDialog
        isOpen={previewDialog.isOpen}
        onClose={handleClosePreview}
        reference={previewDialog.reference}
      /> */}
    </>
  );
};

const ThreadScrollToBottom: FC = () => {
  const { t } = useTranslation();
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip={t("chat.scrollToBottom")}
        variant="outline"
        className="absolute -top-8 rounded-full border-border bg-background hover:bg-accent disabled:invisible"
      >
        <ArrowDownIcon className="h-4 w-4" />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  const isLogsAgentPage = currentPath?.endsWith("/logs");

  return (
    <ThreadPrimitive.Empty>
      <div className="flex w-full max-w-[var(--thread-max-width)] flex-grow flex-col items-center justify-center px-4 py-8">
        {isLogsAgentPage ? (
          <div className="max-w-2xl text-center">
            <h1 className="mb-2 text-2xl font-semibold text-foreground">
              {t("chat.logsEmptyTitle", { defaultValue: "Không có logs" })}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("chat.logsEmptyDescription", {
                defaultValue: "Không có logs hoặc logs rỗng",
              })}
            </p>
          </div>
        ) : (
          <>
            <div className="max-w-2xl text-center">
              <h1 className="mb-2 text-2xl font-semibold text-foreground">
                {t("chat.howCanIHelp")}
              </h1>
              <p className="mb-8 text-sm text-muted-foreground">
                {t("chat.welcomeMessage")}
              </p>
            </div>
            <ThreadWelcomeSuggestions />
          </>
        )}
      </div>
    </ThreadPrimitive.Empty>
  );
};

const ThreadWelcomeSuggestions: FC = () => {
  const threadId = useThreadListItem((t) => t.id);
  const { selectedAgent } = useAgentContext();
  const [suggestionQuestion, setSuggestionQuestion] = useState<{
    suggestions: string[];
  } | null>(null);

  useEffect(() => {
    if (!selectedAgent?.id) return;
    const isExistingThread = isValidUUID(threadId);
    if (!isExistingThread) {
      getSuggestionQuestion(selectedAgent?.id).then(setSuggestionQuestion);
    }
  }, [selectedAgent?.id]);

  if (!suggestionQuestion || suggestionQuestion?.suggestions?.length === 0)
    return;

  return (
    <div className="flex w-full max-w-4xl flex-wrap justify-center gap-3">
      {suggestionQuestion?.suggestions?.map((question: string) => (
        <ThreadPrimitive.Suggestion
          key={question}
          className="flex cursor-pointer items-center justify-center rounded-xl border border-border bg-card p-3 text-left transition-colors duration-200 hover:bg-accent"
          prompt={question}
          method="replace"
          autoSend
        >
          <span className="text-[15px] font-medium text-foreground">
            {question}
          </span>
        </ThreadPrimitive.Suggestion>
      ))}
    </div>
  );
};

const Composer: FC<{ agent: Agent | null }> = ({ agent }) => {
  const { t } = useTranslation();
  const isAgentReady =
    agent &&
    (agent.status === AgentStatus.READY ||
      (agent?.agent_type === AGENT_TYPES.AUTONOMOUS &&
        ![AgentStatus.ERROR, AgentStatus.TRAINING].includes(
          agent?.status as AgentStatus
        ) &&
        agent.is_active)) &&
    !agent.is_archived;

  // Build @ mention suggestions from documents in the Library
  const [documents, setDocuments] = useState<{ id: string; name: string }[]>([]);

  // Fetch top-level Documents folder and list its documents
  // Lightweight approach; can be extended to nested folders or server search if needed
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const foldersResp = await api.get("/folders");
        const rootItems = foldersResp.data as Array<{ id: string; name: string }>
        const docsFolderId = rootItems.find((i) => i.name === "Documents")?.id;
        if (!docsFolderId) return;
        const docsResp = await api.get(`/folders/${docsFolderId}/documents`);
        if (cancelled) return;
        // Filter out folders and only include documents
        const items = (docsResp.data || [])
          .filter((d: any) => d.type === "Document") // Only include documents, exclude folders and workflows
          .map((d: any) => ({ id: d.id, name: d.name }));
        setDocuments(items);
      } catch {
        // ignore for suggestions
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const documentMentionData = (query: string, callback: (results: Array<{ id: string; display: string }>) => void) => {
    const q = (query || "").toLowerCase();
    const results = documents
      .filter((d) => d.name.toLowerCase().includes(q))
      .map((d) => ({ id: d.id, display: d.name }));
    callback(results);
  };

  return (
    <div className="w-full max-w-4xl">
      <ComposerPrimitive.Root className="relative flex w-full items-end rounded-xl border border-border bg-background px-4 py-3 shadow-sm transition-colors duration-200 focus-within:border-ring">
        <ComposerPrimitive.Input asChild>
          {/* We render MentionsInput as the underlying input. We set markup to display-only so the
             parent receives plain text like "@Agent Name" without hidden IDs in the value. */}
          <ComposerMentionsInput
            allowSpaceInQuery
            rows={1}
            autoFocus
            readOnly={!isAgentReady}
            aria-disabled={!isAgentReady}
            tabIndex={!isAgentReady ? -1 : undefined}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (!isAgentReady) e.preventDefault();
            }}
            placeholder={
              !agent
                ? t("chat.selectAgentPlaceholder")
                : !isAgentReady
                ? t("chat.agentNotReady")
                : t("chat.messagePlaceholder")
            }
            className="no-focus-style h-full"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              width: "100%",
            }}
          >
            <Mention
              trigger="@"
              data={documentMentionData}
              markup="@[__display__](__id__)"
              displayTransform={(id, display) => `@${display}`}
              appendSpaceOnAdd
              style={{ backgroundColor: "rgba(59,130,246,0.12)" }}
            />
          </ComposerMentionsInput>
        </ComposerPrimitive.Input>
        <ComposerAction agent={agent} />
      </ComposerPrimitive.Root>
      <div className="mt-2 text-center">
        <p className="text-xs text-muted-foreground">
          {t("chat.aiGeneratedDisclaimer")}
        </p>
      </div>
    </div>
  );
};

const ComposerAction: FC<{ agent: Agent | null }> = ({ agent }) => {
  const { t } = useTranslation();
  const isAgentReady = agent && agent.status === AgentStatus.READY;

  return (
    <>
      <ThreadPrimitive.If running={false}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip={
              !agent
                ? t("chat.selectAgentPlaceholder")
                : !isAgentReady
                ? t("chat.agentNotReady")
                : t("chat.sendMessage")
            }
            variant="ghost"
            disabled={!isAgentReady}
            className="h-full w-8 rounded-lg p-1.5 text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SendHorizontalIcon className="h-4 w-4" />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </ThreadPrimitive.If>
      <ThreadPrimitive.If running>
        <ComposerPrimitive.Cancel asChild>
          <TooltipIconButton
            tooltip={t("chat.stopGenerating")}
            variant="ghost"
            className="h-8 w-8 rounded-lg p-1.5 text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground"
          >
            <CircleStopIcon />
          </TooltipIconButton>
        </ComposerPrimitive.Cancel>
      </ThreadPrimitive.If>
    </>
  );
};

const UserMessage: FC = () => {
  const { t } = useTranslation();
  const messageId = useMessage((m) => m.id);
  const createdAt = useMessage((m) => m.createdAt);
  const thread_id = useThreadListItem((t) => t.remoteId);

  const messages = useThread((t) => t.messages);

  const currentIndex = messages.findIndex((m) => m.id === messageId);

  const assistantMessage =
    currentIndex !== -1 && currentIndex < messages.length - 1
      ? messages[currentIndex + 1]
      : null;

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const messageElement = document.querySelector(
      `[data-message-id="${messageId}"] [data-message-content]`
    );

    const messageText = messageElement?.textContent || "";
    const success = await copyToClipboard(messageText);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sendFavorite = debounce(async () => {
    const userMessageId: string =
      (assistantMessage?.metadata?.custom?.user_message_id as string) ?? "";
    const isExistingThreadId = isValidUUID(thread_id);
    const isExistingMessageId = isValidUUID(userMessageId || messageId);

    if (!isExistingMessageId || !isExistingThreadId) return;
    try {
      await favoriteMessage(thread_id, userMessageId || messageId);
    } catch (error) {
      console.error("Failed to favorite message:", error);
    }
  }, 500);

  return (
    <MessagePrimitive.Root
      className="mx-auto w-full max-w-4xl py-6"
      data-message-id={messageId}
    >
      <div className="flex justify-end px-4">
        <div className="flex w-full max-w-4xl justify-end">
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-end gap-2">
              <div className="flex items-center gap-1">
                <TooltipIconButton
                  tooltip={t("chat.favorite")}
                  variant="ghost"
                  className="h-8 w-8 rounded-lg p-1.5 text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground"
                  onClick={sendFavorite}
                >
                  <Star className="h-4 w-4" />
                </TooltipIconButton>

                <TooltipIconButton
                  tooltip={t("chat.copyMessage")}
                  variant="ghost"
                  className="h-8 w-8 rounded-lg p-1.5 text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <CheckIcon className="h-4 w-4" />
                  ) : (
                    <CopyIcon className="h-4 w-4" />
                  )}
                </TooltipIconButton>

                {/* <ActionBarPrimitive.Edit asChild>
                 <TooltipIconButton
                   tooltip="Edit message"
                   variant="ghost"
                   className="h-8 w-8 p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-200"
                 >
                   <PencilIcon className="h-4 w-4" />
                 </TooltipIconButton>
               </ActionBarPrimitive.Edit> */}
              </div>
              <div
                className="rounded-2xl bg-primary px-4 py-2.5 text-sm leading-relaxed text-primary-foreground"
                data-message-content
              >
                <MessagePrimitive.Parts components={{
                  Text: (props) => <MessageWithMentions content={props.text} />,
                }} />
              </div>
            </div>
            {createdAt && (
              <p className="mt-1 text-xs text-muted-foreground">
                {formatCreatedAt(createdAt)}
              </p>
            )}
          </div>
        </div>
      </div>
      <BranchPicker className="mt-2 flex justify-end px-4" />
    </MessagePrimitive.Root>
  );
};

const EditComposer: FC = () => {
  const { t } = useTranslation();
  return (
    <ComposerPrimitive.Root className="mx-4 my-4 flex w-full max-w-4xl flex-col gap-3 rounded-xl border border-border bg-card p-4">
      <ComposerPrimitive.Input
        className="bg-transparent flex w-full resize-none p-0 text-sm text-foreground outline-none focus:ring-0"
        placeholder={t("chat.editMessagePlaceholder")}
      />

      <div className="flex items-center justify-end gap-2">
        <ComposerPrimitive.Cancel asChild>
          <Button variant="ghost" size="sm">
            {t("common.cancel")}
          </Button>
        </ComposerPrimitive.Cancel>
        <ComposerPrimitive.Send asChild>
          <Button size="sm">{t("chat.saveAndSubmit")}</Button>
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
  );
};

const AssistantMessage: FC = () => {
  const { t } = useTranslation();
  const message = useMessage();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const messageText =
      document.querySelector("[data-assistant-message-content]")?.textContent ||
      "";
    const success = await copyToClipboard(messageText);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <MessagePrimitive.Root className="mx-auto w-full max-w-4xl py-6">
      <div className="flex justify-start px-4">
        <div className="flex w-full max-w-4xl flex-col">
          <div
            className="rounded-2xl bg-muted px-4 py-2.5 text-sm leading-relaxed"
            data-assistant-message-content
          >
            <MessagePrimitive.Parts
              components={{
                Text: MarkdownText,
                Source: (props) => <SourceMessagePart {...props} />,
                Reasoning: (props) => <ReasoningMessagePart {...props} />,
              }}
            />
            <MessageError />
          </div>
          <div className="mt-2 flex items-center gap-1">
            <TooltipIconButton
              tooltip={t("chat.copyMessage")}
              variant="ghost"
              className="h-8 w-8 rounded-lg p-1.5 text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground"
              onClick={handleCopy}
            >
              {copied ? (
                <CheckIcon className="h-4 w-4" />
              ) : (
                <CopyIcon className="h-4 w-4" />
              )}
            </TooltipIconButton>
            {/* <ActionBarPrimitive.Reload asChild>
              <TooltipIconButton 
                tooltip="Regenerate response"
                variant="ghost"
                className="h-8 w-8 p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-200"
              >
                <RefreshCwIcon className="h-4 w-4" />
              </TooltipIconButton>
            </ActionBarPrimitive.Reload> */}
          </div>
          {message.createdAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              {message.createdAt.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
          <ReferencesSection />
        </div>
      </div>
      <BranchPicker className="mt-2 flex justify-start px-4" />
    </MessagePrimitive.Root>
  );
};

const ReferencesSection: FC = () => {
  const { t } = useTranslation();
  // Mock data - replace with actual references from the message
  const references = [
    {
      documentName: "AgentIQ Technical Documentation",
      pages: [1, 3, 5, 7],
    },
    {
      documentName: "API Integration Guide",
      pages: [2, 4, 8],
    },
    {
      documentName: "User Manual v2.1",
      pages: [1, 6, 9, 12],
    },
  ];

  if (references.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg border border-border bg-card p-3">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("chat.references")}
      </h4>
      <div className="space-y-2">
        {references.map((ref, index) => (
          <div key={index} className="flex items-center gap-1 text-xs">
            <button
              className="hover:text-primary/80 font-medium text-primary transition-colors duration-200"
              onClick={() => {}}
            >
              {ref.documentName}
            </button>
            <span className="text-muted-foreground">• {t("chat.pages")}: </span>
            <div className="flex gap-1">
              {ref.pages.map((page, pageIndex) => (
                <button
                  key={pageIndex}
                  className="hover:text-primary/80 text-primary underline transition-colors duration-200"
                  onClick={() => {}}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="bg-destructive/10 dark:bg-destructive/5 mt-2 rounded-md border border-destructive p-3 text-sm text-destructive dark:text-red-200">
        <ErrorPrimitive.Message className="line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const AssistantActionBar: FC = () => {
  const { t } = useTranslation();
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="never"
      autohideFloat="single-branch"
      className="flex items-center gap-1"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton
          tooltip={t("chat.copyMessage")}
          variant="ghost"
          className="h-8 w-8 rounded-lg p-1.5 text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground"
        >
          <MessagePrimitive.If copied>
            <CheckIcon className="h-4 w-4" />
          </MessagePrimitive.If>
          <MessagePrimitive.If copied={false}>
            <CopyIcon className="h-4 w-4" />
          </MessagePrimitive.If>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      {/* <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton 
          tooltip="Regenerate response"
          variant="ghost"
          className="h-8 w-8 p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-200"
        >
          <RefreshCwIcon className="h-4 w-4" />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload> */}
    </ActionBarPrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  const { t } = useTranslation();
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "inline-flex items-center text-xs text-muted-foreground",
        className
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip={t("common.previous")}>
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip={t("common.next")}>
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};

const CircleStopIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      width="16"
      height="16"
    >
      <rect width="10" height="10" x="3" y="3" rx="2" />
    </svg>
  );
};
